/**
 * THE APPLICATION PAGE — the owner's "empty profile page (fill in, status, send to review and so
 * on)", as ONE route whose whole rendering is driven by `Application.state`.
 *
 * WHAT WE IMPROVED ON THAT SHAPE, and why each one is an improvement rather than a preference:
 *
 *   1. THE PAGE LEADS WITH WHAT IS WANTED, not with a blank form. A blank form tells nobody what
 *      getting in takes. The checklist comes from `Application.items` — server data, operator
 *      editable — and each row moves focus to the control that satisfies it.
 *   2. ONE ACTION, ALWAYS, AND NEVER TWO. Every state resolves to a single next step, decided in
 *      `domain/states.ts` so the header, the checklist and the button cannot disagree.
 *   3. PER-SECTION SAVE instead of one "Save profile". The PATCH is partial by contract, so a 422
 *      on the bio does not throw away five other sections of typing.
 *   4. THE PERCENTAGE IS NOT THE GATE AND THE PAGE SAYS SO. Four required rows are. A single bar
 *      implies 100% unlocks the button; it does not.
 *   5. THE TERMS ARE A STEP OF THE FLOW, not a detour to a separate route. The applicant never
 *      leaves the page they are working on.
 *   6. A BROWSER-SIDE DRAFT FOR THE LONG FIELDS, because `bioLong` is a 600–1200 band and a
 *      half-written bio cannot be saved to the server at all.
 *
 * WHAT IT MUST NOT DO: promise a turnaround time (there is no SLA and no operation returns one),
 * compute a completeness percentage, or render a rejection (there is no terminal refused state).
 *
 * NOTHING LOCKS THE PROFILE WHILE THE APPLICATION IS IN REVIEW — `updateOwnProfile` declares no
 * state gate other than SLUG_FROZEN, and `in_review` appears on no profile or application path.
 * So an applicant may keep editing while a reviewer is looking, and the page says so with a quiet
 * note rather than a lock.
 */
import { useCallback, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { Alert, Anchor, Group, Stack, Text } from '@mantine/core';
import type { TFunction } from 'i18next';
import { useTranslation } from '../../../i18n';
import type { Application, OwnProfile, StoredImage } from '../contract';
import { humanMessage } from '../api/problem';
import { useApplicationData } from '../data/useApplicationData';
import { useApplicationActions } from '../data/useApplicationActions';
import { useProfileForm } from '../data/useProfileForm';
import { useSlugCheck } from '../data/useSlugCheck';
import { useAvatarUpload, useCredentialUpload } from '../data/useUpload';
import { FIELD_ANCHORS, requiredOutstanding, rowsGateSending } from '../domain/completeness';
import { LIMITS, VISIBILITY_FIELDS } from '../domain/limits';
import type { VisibilityKey } from '../domain/limits';
import { addressRule, nextStep, slugIsEditable } from '../domain/states';
import { AvatarField } from '../components/AvatarField';
import { Checklist } from '../components/Checklist';
import { CredentialsPanel } from '../components/CredentialsPanel';
import { DecisionNotice } from '../components/DecisionNotice';
import { ExpertisePicker, TopicsField } from '../components/ExpertisePicker';
import { TextAreaField, TextField, VisibilitySwitch } from '../components/Fields';
import { FormSection } from '../components/FormSection';
import { LinksEditor } from '../components/LinksEditor';
import { PageFrame, PhotographMark, ProfileColumns, SideCard } from '../components/PageFrame';
import { SlugField } from '../components/SlugField';
import { ErrorState, LoadingState } from '../components/States';
import { StatusHeader } from '../components/StatusHeader';
import { TermsGate } from './TermsGate';

export interface ApplicationScreenProps {
  /** The way into the cabinet, given only where the page is drawn outside it (`ApplyFrame`). */
  readonly onOpenCabinet?: (() => void) | undefined;
}

export function ApplicationScreen({ onOpenCabinet }: ApplicationScreenProps): ReactNode {
  const { t } = useTranslation('application');
  const data = useApplicationData();
  const [showTerms, setShowTerms] = useState(false);
  const [highlightChecklist, setHighlightChecklist] = useState(false);
  const [banner, setBanner] = useState<BannerKey | null>(null);
  const formTop = useRef<HTMLDivElement>(null);

  const onSaved = useCallback(
    (saved: OwnProfile) => {
      data.setProfile(saved);
      // A save can satisfy a checklist row, which moves the percentage and possibly the state.
      data.reloadProfileAndApplication();
    },
    [data],
  );

  const actions = useApplicationActions({
    onApplication: data.setApplication,
    onTerms: data.setTerms,
    onReload: data.reload,
  });

  const slugEditable = slugIsEditable(data.profile?.slugFrozen);
  const form = useProfileForm({ profile: data.profile, slugEditable, onSaved });

  const onAvatarClaimed = useCallback(
    (_image: StoredImage) => {
      // Not in the form draft: the claim IS the save, so both are re-read, not patched locally.
      data.reloadProfileAndApplication();
    },
    [data],
  );
  const avatar = useAvatarUpload(onAvatarClaimed);

  const onCredentialClaimed = useCallback(() => {
    data.credentials.reload();
    data.reloadProfileAndApplication();
  }, [data]);
  const credentialUpload = useCredentialUpload(onCredentialClaimed);

  const slugCheck = useSlugCheck(form.draft.slug, slugEditable, data.profile?.displayName ?? null);

  const jump = useCallback((anchorId: string) => {
    const node = document.getElementById(anchorId);
    if (node === null) return;
    node.scrollIntoView({ behavior: 'smooth', block: 'center' });
    // Focus, not just scroll: a keyboard user who followed a checklist row must LAND on the
    // control, not merely have it scrolled past them. Most anchors are wrapper elements that
    // cannot take focus, so fall through to the first control inside — `focus()` on a
    // non-focusable element does nothing and reports nothing, which is exactly the silent failure
    // that makes a keyboard-only pass look fine and behave badly.
    node.focus({ preventScroll: true });
    if (document.activeElement === node) return;
    const focusable = node.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, select, button, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus({ preventScroll: true });
  }, []);

  // ---- the three whole-page states -------------------------------------------------------

  if (data.status === 'loading') {
    return (
      <PageFrame outside={onOpenCabinet !== undefined} size="lg">
        <Stack gap="xl">
          <LoadingState label={t('screen.loading.application')} lines={4} />
          <LoadingState label={t('screen.loading.form')} lines={8} />
        </Stack>
      </PageFrame>
    );
  }

  if (data.status === 'error' || data.application === undefined || data.profile === undefined) {
    return (
      <PageFrame outside={onOpenCabinet !== undefined} size="sm">
        <ErrorState error={data.error} fallback={t('screen.loadFailed')} onRetry={data.reload} />
      </PageFrame>
    );
  }

  const application = data.application;
  const profile = data.profile;
  const terms = data.terms;
  const owed = owedForSubmission(data.agreements.data, terms);
  const outstanding = requiredOutstanding(application);
  const step = stepFor(application, owed, outstanding, slugEditable, onOpenCabinet, t);

  // ---- the last step, which is part of this flow rather than a detour ---------------------

  if (showTerms) {
    // An applicant meets "Our standards and agreement" (design 25), whose "Submit for review" is
    // the header's own action, run again; an approved author reads the author agreement. Both are
    // drawn by `LastStep`, at the foot, so the lines below that are cited by number keep their
    // place.
    return (
      <PageFrame outside={onOpenCabinet !== undefined} size="sm">
        <LastStep
          approved={step.situation === 'approved'}
          data={data}
          actions={actions}
          owed={owed}
          submitLabel={step.action?.label ?? t('status.ready.action')}
          onSubmit={runAction}
          onBack={() => {
            actions.clearError();
            setShowTerms(false);
          }}
          onTermsAccepted={() => {
            setShowTerms(false);
            setBanner(termsAcceptedBanner(step.situation === 'approved'));
          }}
        />
      </PageFrame>
    );
  }

  // ---- the single action --------------------------------------------------------------------

  // The step's one button (`nextStep`). No action accepts the agreements alone: while any is owed,
  // Submit and Resubmit open them first ("Our standards and agreement"), and the send carries
  // what was accepted.
  function runAction(): void {
    if (step.action === null) return;
    switch (step.action.kind) {
      case 'fill_in':
        jump(outstanding > 0 ? firstUnsatisfiedAnchor() : FIELD_ANCHORS.displayName);
        return;
      case 'open_cabinet':
        onOpenCabinet?.();
        return;
      case 'submit':
      case 'resubmit': {
        if (terms === undefined || owed === undefined || (owed.length > 0 && !showTerms)) {
          setShowTerms(true);
          return;
        }
        const send = step.action.kind === 'submit' ? actions.submit : actions.resubmit;
        void send(terms.versionLabel, owed).then((outcome) => {
          switch (outcome.kind) {
            case 'done':
              setBanner('banner.submitted');
              setHighlightChecklist(false);
              setShowTerms(false);
              return;
            case 'needs_terms':
              setBanner(null);
              setShowTerms(true);
              return;
            case 'incomplete':
              setHighlightChecklist(true);
              setBanner(null);
              setShowTerms(false);
              formTop.current?.focus();
              return;
            case 'stale':
              setBanner(null);
              return;
            default:
              return;
          }
        });
        return;
      }
      default:
        return;
    }
  }

  function firstUnsatisfiedAnchor(): string {
    const row = application.items.find((item) => item.isRequiredToSubmit && !item.isSatisfied);
    if (row === undefined) return FIELD_ANCHORS.displayName;
    const map: Record<string, string> = {
      display_name: FIELD_ANCHORS.displayName,
      avatar: FIELD_ANCHORS.avatar,
      bio_long: FIELD_ANCHORS.bioLong,
      expertise: FIELD_ANCHORS.expertise,
    };
    return map[row.fieldCode] ?? FIELD_ANCHORS.displayName;
  }

  function visibilityFooter(key: VisibilityKey): ReactNode {
    const meta = VISIBILITY_FIELDS.find((field) => field.key === key);
    // The seeded default is the fallback, not `true`: the time zone's default is PRIVATE, and
    // defaulting it to public here would show a person's location because a property was absent.
    const current = form.draft.visibility[key] ?? meta?.defaultPublic ?? true;
    return (
      <VisibilitySwitch
        checked={current}
        label={t(`visibility.${key}`)}
        onChange={(next) => {
          // Saves itself — see the note on `setVisibility`. It belongs to no section, because the
          // switches straddle three of them.
          form.setVisibility(key, next);
        }}
      />
    );
  }

  const editingWhileInReview = step.situation === 'in_review';
  const outside = onOpenCabinet !== undefined;

  return (
    <PageFrame outside={outside} size="lg">
      <Stack gap="lg">
        <div ref={formTop} tabIndex={-1} />

        <StatusHeader
          application={application}
          step={step}
          busy={actions.busy}
          onAction={runAction}
          note={
            terms !== undefined && terms.blocksSubmission === true ? (
              <Text size="xs" c="dimmed" ta="right" style={{ maxWidth: '18rem' }}>
                {t('screen.heldNote')}
              </Text>
            ) : null
          }
        />

        {banner !== null ? (
          <Alert
            variant="light"
            role="status"
            withCloseButton
            onClose={() => {
              setBanner(null);
            }}
            style={{ borderColor: 'oklch(var(--mx-success) / 0.35)' }}
          >
            {t(banner)}
          </Alert>
        ) : null}

        {actions.lastError !== null && actions.lastError !== undefined ? (
          <ErrorState
            error={actions.lastError}
            fallback={t('agreement.submitFailed')}
            onRetry={runAction}
            title={t('screen.submitFailedTitle')}
          />
        ) : null}

        {form.visibilityError !== null && form.visibilityError !== undefined ? (
          <Alert
            variant="light"
            role="alert"
            style={{ borderColor: 'oklch(var(--mx-danger) / 0.35)' }}
          >
            <Text size="sm">
              {humanMessage(form.visibilityError, t('screen.switchNotSaved'), undefined, t)}
            </Text>
          </Alert>
        ) : null}

        {application.decision !== null && application.decision !== undefined ? (
          <DecisionNotice decision={application.decision} round={application.round} />
        ) : null}

        {editingWhileInReview ? (
          <Alert variant="light" role="note">
            <Text size="sm">{t('screen.editingInReview')}</Text>
          </Alert>
        ) : null}

        {form.rescuedKeys.length > 0 ? (
          <Alert
            variant="light"
            role="status"
            style={{ borderColor: 'oklch(var(--mx-warning) / 0.35)' }}
          >
            <Stack gap={4}>
              <Text size="sm">{t('screen.rescue.body', { min: LIMITS.bioLong.min })}</Text>
              <Group gap="xs">
                {form.rescuedKeys.map((key) => (
                  <Anchor
                    key={key}
                    component="button"
                    type="button"
                    size="xs"
                    onClick={() => {
                      form.discardRescue(key);
                    }}
                  >
                    {t('screen.rescue.discard', { field: rescuedFieldName(t, key) })}
                  </Anchor>
                ))}
              </Group>
            </Stack>
          </Alert>
        ) : null}

        <ProfileColumns
          form={
            <>
              {/* The profile photo leads, as the prototype's portrait card does ---------- */}
              <FormSection
                icon={<PhotographMark />}
                anchorId="apply-section-photograph"
                title={t('screen.photograph.title')}
                audience="public"
                dirty={false}
                state={form.sectionState('photograph')}
                onSave={() => undefined}
                savable={false}
              >
                <AvatarField
                  id={FIELD_ANCHORS.avatar}
                  avatarUrl={profile.avatarUrl}
                  displayName={form.draft.displayName}
                  upload={avatar}
                />
              </FormSection>

              {/* 1 ------------------------------------------------------------------------- */}
              <FormSection
                index={1}
                anchorId="apply-section-identity"
                title={t('screen.identity.title')}
                hint={t('screen.identity.hint')}
                audience="public"
                dirty={form.isSectionDirty('identity')}
                state={form.sectionState('identity')}
                onSave={() => {
                  void form.saveSection('identity');
                }}
              >
                <Stack gap="lg">
                  <TextField
                    id={FIELD_ANCHORS.displayName}
                    label={t('screen.identity.displayName.label')}
                    description={t('screen.identity.displayName.description')}
                    placeholder={t('screen.identity.displayName.placeholder')}
                    requiredToSubmit={rowsGateSending(application)}
                    value={form.draft.displayName}
                    error={form.errors.displayName}
                    onChange={(value) => {
                      form.setField('displayName', value);
                    }}
                  />

                  <TextField
                    id={FIELD_ANCHORS.headline}
                    label={t('screen.identity.headline.label')}
                    description={t('screen.identity.headline.description')}
                    placeholder={t('screen.identity.headline.placeholder')}
                    value={form.draft.headline}
                    error={form.errors.headline}
                    maxCount={LIMITS.headline.max}
                    footer={visibilityFooter('headline')}
                    onChange={(value) => {
                      form.setField('headline', value);
                    }}
                  />

                  <SlugField
                    id={FIELD_ANCHORS.slug}
                    value={form.draft.slug}
                    rule={addressRule(step.situation, !slugEditable)}
                    error={form.errors.slug}
                    check={slugCheck}
                    onChange={(value) => {
                      form.setField('slug', value);
                    }}
                  />

                  <Group align="flex-start" gap="lg" grow>
                    <TextField
                      id="apply-pronouns"
                      label={t('screen.identity.pronouns.label')}
                      description={t('screen.identity.pronouns.description')}
                      placeholder={t('screen.identity.pronouns.placeholder')}
                      value={form.draft.pronouns}
                      error={form.errors.pronouns}
                      maxCount={LIMITS.pronouns.max}
                      footer={visibilityFooter('pronouns')}
                      onChange={(value) => {
                        form.setField('pronouns', value);
                      }}
                    />
                    {/* "Primary location" (design 25; T27) where "Time zone" was: picked, not typed.
                        The pick sets the zone, which stays on file with no field of its own; its
                        switch is no longer drawn. */}
                    <PrimaryLocationField
                      id="apply-primary-location"
                      value={form.draft.primaryLocation}
                      error={form.errors.primaryLocation}
                      footer={visibilityFooter('primaryLocation')}
                      onChange={(next) => {
                        form.setField('primaryLocation', next);
                      }}
                    />
                  </Group>
                </Stack>
              </FormSection>

              {/* 2 ------------------------------------------------------------------------- */}
              <FormSection
                index={2}
                anchorId="apply-section-about"
                title={t('screen.about.title')}
                hint={aboutHint(rowsGateSending(application), t)}
                audience="public"
                dirty={form.isSectionDirty('about')}
                state={form.sectionState('about')}
                onSave={() => {
                  void form.saveSection('about');
                }}
              >
                <Stack gap="lg">
                  <TextAreaField
                    id={FIELD_ANCHORS.bioLong}
                    label={t('screen.about.bioLong.label')}
                    description={t('screen.about.bioLong.description', {
                      min: LIMITS.bioLong.min,
                      max: LIMITS.bioLong.max,
                    })}
                    placeholder={t('screen.about.bioLong.placeholder')}
                    requiredToSubmit={rowsGateSending(application)}
                    minRows={8}
                    value={form.draft.bioLong}
                    error={form.errors.bioLong}
                    minCount={LIMITS.bioLong.min}
                    maxCount={LIMITS.bioLong.max}
                    footer={visibilityFooter('bioLong')}
                    onChange={(value) => {
                      form.setField('bioLong', value);
                    }}
                  />

                  <TextAreaField
                    id={FIELD_ANCHORS.bioShort}
                    label={t('screen.about.bioShort.label')}
                    description={t('screen.about.bioShort.description')}
                    value={form.draft.bioShort}
                    error={form.errors.bioShort}
                    maxCount={LIMITS.bioShort.max}
                    minRows={2}
                    onChange={(value) => {
                      form.setField('bioShort', value);
                    }}
                  />

                  <TextAreaField
                    id={FIELD_ANCHORS.about}
                    label={t('screen.about.about.label')}
                    description={t('screen.about.about.description')}
                    value={form.draft.about}
                    error={form.errors.about}
                    maxCount={LIMITS.about.max}
                    onChange={(value) => {
                      form.setField('about', value);
                    }}
                  />

                  <TextAreaField
                    id="apply-experience"
                    label={t('screen.about.experience')}
                    description={t('screen.about.experienceDescription')}
                    value={form.draft.experience}
                    error={form.errors.experience}
                    maxCount={LIMITS.experience.max}
                    footer={visibilityFooter('experience')}
                    onChange={(value) => {
                      form.setField('experience', value);
                    }}
                  />

                  <TextAreaField
                    id="apply-approach"
                    label={t('screen.about.approach')}
                    description={t('screen.about.approachDescription')}
                    value={form.draft.approach}
                    error={form.errors.approach}
                    maxCount={LIMITS.approach.max}
                    footer={visibilityFooter('approach')}
                    onChange={(value) => {
                      form.setField('approach', value);
                    }}
                  />

                  <TextAreaField
                    id="apply-education"
                    label={t('screen.about.education')}
                    description={t('screen.about.educationDescription')}
                    value={form.draft.education}
                    error={form.errors.education}
                    maxCount={LIMITS.education.max}
                    footer={visibilityFooter('education')}
                    onChange={(value) => {
                      form.setField('education', value);
                    }}
                  />
                </Stack>
              </FormSection>

              {/* 3 ------------------------------------------------------------------------- */}
              <FormSection
                index={3}
                anchorId="apply-section-expertise"
                title={t('screen.expertise.title')}
                hint={t('screen.expertise.hint')}
                audience="mixed"
                dirty={form.isSectionDirty('expertise')}
                state={form.sectionState('expertise')}
                onSave={() => {
                  void form.saveSection('expertise');
                }}
              >
                <Stack gap="lg">
                  <ExpertisePicker
                    id={FIELD_ANCHORS.expertise}
                    applying={rowsGateSending(application)}
                    value={form.draft.expertise}
                    catalogue={data.categories.data}
                    loading={data.categories.status === 'loading'}
                    loadError={data.categories.status === 'error' ? data.categories.error : null}
                    onRetry={data.categories.reload}
                    onChange={(next) => {
                      form.setField('expertise', next);
                    }}
                  />
                  <TopicsField
                    id={FIELD_ANCHORS.tags}
                    value={form.draft.tags}
                    error={form.errors.tags}
                    onChange={(next) => {
                      form.setField('tags', next);
                    }}
                  />
                  {/* "Years of practice" (design 25; T25): shown whenever a number is set, with no
                      switch (the owner, 2026-10-19). */}
                  <YearsOfPracticeField
                    value={form.draft.yearsOfPractice}
                    error={form.errors.yearsOfPractice}
                    onChange={(value) => {
                      form.setField('yearsOfPractice', value);
                    }}
                  />
                </Stack>
              </FormSection>

              {/* 4 ------------------------------------------------------------------------- */}
              <FormSection
                index={4}
                anchorId="apply-section-links"
                title={t('screen.links.title')}
                hint={t('screen.links.hint')}
                audience="mixed"
                dirty={form.isSectionDirty('links')}
                state={form.sectionState('links')}
                onSave={() => {
                  void form.saveSection('links');
                }}
              >
                <Stack gap="xl">
                  <LinksEditor
                    id={FIELD_ANCHORS.links}
                    applying={rowsGateSending(application)}
                    value={form.draft.links}
                    errors={form.errors}
                    onChange={(next) => {
                      form.setField('links', next);
                    }}
                  />
                  <CredentialsPanel
                    id={FIELD_ANCHORS.credentials}
                    applying={rowsGateSending(application)}
                    items={data.credentials.data?.items}
                    loading={data.credentials.status === 'loading'}
                    loadError={data.credentials.status === 'error' ? data.credentials.error : null}
                    onRetryList={data.credentials.reload}
                    upload={credentialUpload}
                  />
                </Stack>
              </FormSection>

              {/* 5 ------------------------------------------------------------------------- */}
              <FormSection
                index={5}
                anchorId={FIELD_ANCHORS.privateBlock}
                title={t('screen.private.title')}
                hint={t('screen.private.hint')}
                audience="private"
                dirty={form.isSectionDirty('private')}
                state={form.sectionState('private')}
                onSave={() => {
                  void form.saveSection('private');
                }}
              >
                <Stack gap="lg">
                  <TextField
                    id="apply-private-email"
                    label={t('screen.private.email.label')}
                    description={t('screen.private.email.description')}
                    value={form.draft.privateEmail}
                    error={form.errors.privateEmail}
                    maxCount={LIMITS.privateEmail.max}
                    onChange={(value) => {
                      form.setField('privateEmail', value);
                    }}
                  />
                  {/* No "When you can write" (design 24, T23) and no "Let readers write to me
                      through MoonX" (the owner, 2026-10-19: this block is for the editorial team
                      only). The server keeps both columns and the form's model keeps their values,
                      which nothing here changes, so no save sends them. */}
                  <Text size="xs" c="dimmed">
                    {t('screen.private.pay')}
                  </Text>
                </Stack>
              </FormSection>

              {/* "Your chart" (design 24, T19): the last card, saved by its own button, and only
                  beside the sidebar - where Settings showed it, to an author with a cabinet. */}
              {outside ? null : <BirthDataSection initial={profile.signs} />}

              {/* Payment details (design 25; T22's D1): a card of their own at the end of the page -
                  the summary Payments shows, and the way to their page, never fields among the
                  profile's. Beside the sidebar only, where the payout read answers. */}
              {outside ? null : <PaymentDetailsSummaryCard />}
            </>
          }
          side={
            <>
              {/* "Preview your public page", and the live one for an approved author (T28). */}
              <PublicPageLinks slug={profile.slug} live={step.situation === 'approved'} />

              <Checklist
                application={application}
                onJump={jump}
                highlightUnsatisfied={highlightChecklist}
              />

              {terms !== undefined ? (
                <SideCard>
                  <Stack gap={6}>
                    <Text size="sm" fw={700}>
                      {terms.accepted
                        ? t('screen.side.termsAccepted', { version: terms.versionLabel })
                        : t('screen.side.termsNotAccepted', { version: terms.versionLabel })}
                    </Text>
                    {/* An applicant accepts it on the last step, when submitting (design 25): here
                        it is only read. An approved author reads, or accepts, it on its own page. */}
                    {step.situation === 'approved' ? (
                      <Anchor
                        component="button"
                        type="button"
                        size="xs"
                        ta="left"
                        onClick={() => {
                          setShowTerms(true);
                        }}
                      >
                        {terms.accepted
                          ? t('screen.side.readAgain')
                          : t('screen.side.readAndAccept')}
                      </Anchor>
                    ) : (
                      <Anchor
                        href={terms.contentUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        size="xs"
                      >
                        {terms.accepted ? t('screen.side.read') : t('screen.side.acceptOnSubmit')}
                      </Anchor>
                    )}
                  </Stack>
                </SideCard>
              ) : null}

              {data.isRefreshing ? (
                <Text size="xs" c="dimmed" aria-live="polite">
                  {t('screen.side.refreshing')}
                </Text>
              ) : null}

              {form.anySaving ? (
                <Text size="xs" c="dimmed" aria-live="polite">
                  {t('screen.side.saving')}
                </Text>
              ) : null}

              {actions.lastError !== null && actions.lastError !== undefined ? (
                <Text size="xs" role="alert" style={{ color: 'oklch(var(--mx-danger))' }}>
                  {humanMessage(
                    actions.lastError,
                    t('screen.side.somethingWentWrong'),
                    undefined,
                    t,
                  )}
                </Text>
              ) : null}
            </>
          }
        />
      </Stack>
    </PageFrame>
  );
}

/* ============================================================== written at the foot, on purpose */

/*
 * ★ DESIGN 15 (the owner's live look at real cabinets, 2026-10-01): the page is drawn as the
 * prototype's "Your author profile" - the status header and the checklist as calm cards, the
 * photograph's card first (the prototype's portrait card), then the five numbered sections, each a
 * card with a header strip saying who sees it: "Public", "Private" or "Partly public"
 * (../components/FormSection.tsx). `PageFrame` (../components/PageFrame.tsx) draws the page bare
 * inside the cabinet and with the stars and the MoonX mark outside it, told apart by the same
 * `onOpenCabinet` that `stepFor` below reads. Nothing a section reads or writes changed.
 */

/**
 * `nextStep`'s input, assembled down here rather than inline because the lines above are cited by
 * number (web/ui/test/e2e/application-form-accessibility.spec.ts names this file's "Author name" and
 * "Experience" fields by line), and the input outgrew its one line. `owed` is the submission
 * documents still to accept (../domain/agreements.ts); none owed is the old `terms.accepted`.
 *
 * `slugFrozen` is the address field's own answer, `!slugEditable` - the same value that decides
 * whether the field can be typed in - so the header's sentence about the address and the field
 * under it cannot disagree (design 14, finding F3: an author the import brought over was told the
 * address "is now fixed" above an empty field they could still fill in).
 *
 * `insideCabinet` is read off `onOpenCabinet`: the router hands the page a way into the cabinet
 * only where it draws the page outside it (`ApplyFrame`, src/router.tsx), so a page with none is
 * beside the sidebar already, and an approved author's header offers no button there.
 */
function stepFor(
  application: Application,
  owed: readonly SubmissionDocument[] | undefined,
  outstanding: number,
  slugEditable: boolean,
  onOpenCabinet: (() => void) | undefined,
  t: TFunction<'application'>,
) {
  return nextStep(
    {
      application,
      // "Accepted" is nothing owed for a submit: every submission document (design 25).
      termsAccepted: owed === undefined ? undefined : owed.length === 0,
      requiredOutstanding: outstanding,
      slugFrozen: !slugEditable,
      insideCabinet: onOpenCabinet === undefined,
    },
    t,
  );
}

/**
 * The hint over "In your own words". An applicant is told who reads it first - an editor - which
 * is the honest reason the long bio is one of the four rows. An approved author has no reviewer:
 * the owner's live walk of real cabinets (2026-10-01) read "The part a reviewer actually reads" on
 * the profiles of authors with a hundred published articles. For them the readers are the readers.
 * `applying` is `rowsGateSending`, the one answer this page already takes for "is this still an
 * application" (domain/completeness.ts).
 */
function aboutHint(applying: boolean, t: TFunction<'application'>): string {
  return applying ? t('screen.about.hint.applying') : t('screen.about.hint.author');
}

/**
 * What the page says once the terms are accepted. An applicant has an application to send; an
 * approved author has none (design 14, F3), and for them the acceptance is what lets articles be
 * sent again - so they are told only that it went through.
 */
function termsAcceptedBanner(approved: boolean): BannerKey {
  return approved ? 'banner.termsAccepted' : 'banner.termsAcceptedApplicant';
}

/** The page's one success line, as its `application` catalogue key. */
type BannerKey = 'banner.submitted' | 'banner.termsAccepted' | 'banner.termsAcceptedApplicant';

/*
 * ★ DESIGN 24'S IMPORTS ARE AT THE FOOT, NOT THE HEAD, ON PURPOSE: lines of this file are cited
 * by number (web/ui/test/e2e/application-form-accessibility.spec.ts names three ranges of it), so
 * the imports the round added are declared below them. Imports are hoisted, so where they are
 * written changes nothing about when they load.
 */
import { BirthDataSection } from '../components/BirthDataSection';
import { PublicPageLinks } from '../components/PublicPageLinks';

/**
 * ★ THE LAST STEP (design 25): "Our standards and agreement" for an applicant - its "Submit for
 * review" runs the header's action again, which now finds the step open and accepts and submits in
 * one press - and the author agreement's own page for an approved author, who submits nothing here
 * and reads or accepts the agreement alone. Drawn down here so the cited lines above keep theirs.
 */
function LastStep({
  approved,
  data,
  actions,
  owed,
  submitLabel,
  onSubmit,
  onBack,
  onTermsAccepted,
}: {
  readonly approved: boolean;
  readonly data: ApplicationData;
  readonly actions: ApplicationActions;
  readonly owed: readonly SubmissionDocument[] | undefined;
  readonly submitLabel: string;
  readonly onSubmit: () => void;
  readonly onBack: () => void;
  readonly onTermsAccepted: () => void;
}): ReactNode {
  const terms = data.terms;
  if (!approved) {
    return (
      <AgreementStep
        documents={owed}
        loadError={data.error}
        onRetry={data.reload}
        submitLabel={submitLabel}
        onSubmit={onSubmit}
        busy={actions.busy}
        submitError={actions.lastError}
        onBack={onBack}
      />
    );
  }
  return (
    <TermsGate
      terms={terms}
      loading={terms === undefined}
      error={data.error}
      onRetry={data.reload}
      accepting={actions.busy}
      acceptError={actions.lastError}
      audience="existing_author"
      onBack={onBack}
      onAccept={() => {
        if (terms === undefined) return;
        void actions.acceptTerms(terms.versionLabel).then((outcome) => {
          if (outcome.kind === 'done') {
            onTermsAccepted();
          }
        });
      }}
    />
  );
}

import { AgreementStep } from './AgreementStep';
import { owedForSubmission } from '../domain/agreements';
import type { SubmissionDocument } from '../domain/agreements';
import type { ApplicationData } from '../data/useApplicationData';
import type { ApplicationActions } from '../data/useApplicationActions';
import { PrimaryLocationField } from '../components/PrimaryLocationField';
import { YearsOfPracticeField } from '../components/YearsOfPracticeField';
import { PaymentDetailsSummaryCard } from '../../../routes/payments/PaymentDetailsSummaryCard';

/** The rescued field's own label on this screen, as each language writes it (German capitalises nouns). */
function rescuedFieldName(t: TFunction<'application'>, key: string): string {
  switch (key) {
    case 'bioLong':
      return t('screen.rescue.fullBio');
    case 'bioShort':
      return t('screen.about.bioShort.label');
    case 'about':
      return t('screen.about.about.label');
    case 'experience':
      return t('screen.about.experience');
    case 'approach':
      return t('screen.about.approach');
    case 'education':
      return t('screen.about.education');
    default:
      return key;
  }
}
